import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetAllWarehouseCardsQuery } from 'services/wareHouseCard.service';
import { setTableParams, TableCacheItem } from 'stores/slices/tableCacheSlice';
import { QueryParams } from 'types';
import { WarehouseCardProductRecord } from 'types/ware-house-card-product.type';
import { formatCurrency } from 'utils';
import { buildFullParams } from 'utils/filterBuilder';

export const useWareHouseCardOperations = () => {
  const pageKey = 'warehouse-card-list';
  const dispatch = useAppDispatch();
  const tableState = useAppSelector((s) => s.tableCache[pageKey]) as
    | TableCacheItem<WarehouseCardProductRecord>
    | undefined;
  const { productId } = useParams();
  const [filteredQuery, setFilteredQuery] = useState<QueryParams>({
    size: tableState?.pageSize ?? 10,
    page: tableState?.page ?? 1,
    sort: tableState?.sortField
      ? `${String(tableState.sortField)}:${tableState.sortOrder === 'descend' ? 'desc' : 'asc'}`
      : 'createdAt:desc',
    filter: tableState?.filter ?? '',
    include: 'staff.baseUser',
    productId: productId as string,
  });
  const { data: warehouseCardProductData, isLoading: warehouseCardProductLoading } = useGetAllWarehouseCardsQuery(
    { query: filteredQuery },
    { skip: !productId },
  );
  const [wareHouseData, setWareHouseData] = useState<WarehouseCardProductRecord[]>([]);
  const handlePagination = (query: QueryParams) => {
    const columnDate = ['transactionDate'];
    const filterQuery = buildFullParams(query, undefined, undefined, undefined, columnDate);
    setFilteredQuery((prev) => ({
      ...prev,
      ...filterQuery,
    }));
  };
  useEffect(() => {
    if (warehouseCardProductData) {
      const mappedWareHouseData: WarehouseCardProductRecord[] = warehouseCardProductData?.data?.rows?.map((item) => {
        return {
          id: item.id,
          transactionDate: item.transactionDate ? dayjs(item.transactionDate).format('DD/MM/YYYY') : '',
          transactionDateRaw: item.transactionDate,
          note: item.note || '',
          costPrice: formatCurrency(Number(item.costPrice) || 0) || 0,
          quantityIn: formatCurrency(Number(item.quantityIn || 0)),
          quantityOut: formatCurrency(Number(item.quantityOut || 0)),
          balanceAfter: formatCurrency(Number(item.balanceAfter || 0)),
          createdBy: item.createdBy || '',
          createdAt: item.createdAt || '',
          refCode: item.refCode || '',
          warehouseId: item.warehouseId || '',
          productId: item.productId || '',
          totalCost: formatCurrency(Number(item.totalCost || 0)),
          referenceType: item.referenceType || '',
          refId: item.refId || '',
          referenceLineId: item.referenceLineId || '',
          staffNameCode: `${item.staff?.baseUser?.code} - ${item.staff?.baseUser?.name}` || '',
          staff: item?.staff,
        };
      });
      setWareHouseData(mappedWareHouseData);
      dispatch(
        setTableParams({
          pageKey,
          params: {
            lastFetched: Date.now(),
            search: filteredQuery.searchValue ?? '',
            page: filteredQuery.page ?? 1,
            pageSize: filteredQuery.size ?? 10,
            filter: filteredQuery.filter ?? '',
          } as unknown as Partial<TableCacheItem<unknown>>,
        }),
      );
    }
  }, [warehouseCardProductData]);
  return {
    tableState,
    filteredQuery,
    handlePagination,
    warehouseCardProductData,
    warehouseCardProductLoading,
    wareHouseData,
  };
};
